/**
 * A word-level diff for an edit proposal: what goes, what comes, and what
 * stays — the longest common run of words.
 */

export interface DiffPart {
  kind: 'same' | 'removed' | 'added';
  text: string;
}

/** Past this many word pairs, the edit is shown whole: before, then after. */
const MAX_CELLS = 250_000;

/** Each word with the space after it, so a run of changed words reads whole. */
function tokens(text: string): string[] {
  return text.split(/(?<=\s)(?=\S)/).filter((t) => t !== '');
}

function push(parts: DiffPart[], kind: DiffPart['kind'], text: string) {
  const last = parts.at(-1);
  if (last?.kind === kind) {
    last.text += text;
  } else {
    parts.push({ kind, text });
  }
}

export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length * b.length > MAX_CELLS) {
    return [
      ...(before ? [{ kind: 'removed' as const, text: before }] : []),
      ...(after ? [{ kind: 'added' as const, text: after }] : []),
    ];
  }
  // lcs[i][j]: the longest common run of a[i..] and b[j..].
  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const parts: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push(parts, 'same', a[i]);
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push(parts, 'removed', a[i]);
      i += 1;
    } else {
      push(parts, 'added', b[j]);
      j += 1;
    }
  }
  for (; i < a.length; i += 1) {
    push(parts, 'removed', a[i]);
  }
  for (; j < b.length; j += 1) {
    push(parts, 'added', b[j]);
  }
  return parts;
}
